import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Table,
  Button,
  Spin,
  Alert,
  Typography,
  Space,
  Card,
  Row,
  Col,
  Tooltip,
  Result
} from "antd";
import {
  uploadOrderItemsExcel,
  downloadOrderItemFile,
  getOrderItemsByOrderId,
  downloadOrderPdf,
  updateOrderItemStatus,
  updateOrderItem,
  createOrderItem,
  deleteOrderItem,
} from "../../../services/orderItemService";
import { getOrderById, updateOrderStatus } from "../../../services/orderService";
import { getEmployeeByAdminId } from "../../../services/employeeService";
import { FilePdfOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import CreateOrderItemModal from "../../../components/modals/CreateOrderItemModal";
import UpdateOrderItemModal from "../../../components/modals/UpdateOrderItemModal";
import ConfirmDeleteModal from "../../../components/common/ConfirmDeleteModal";
import ExcelUploadSection from "../components/ExcelUploadSection";
import StatusSelect from "../../../components/common/StatusSelect";
import { useApiCall } from "../../../hooks/useApiCall";
import { toast } from 'react-toastify';
import { formatPrice, formatTotalPrice } from "../../../utils/formatters";
import { getUnitCodeLabel } from "../../../constants/invoiceConstants";

const { Title, Text } = Typography;

const AdminOrderItemsPage = () => {
  const { orderId } = useParams();
  const [orderItems, setOrderItems] = useState([]);
  const [order, setOrder] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newItem, setNewItem] = useState({
    itemName: "",
    quantity: 1,
    unitPrice: "",
    taxRate: null,
    unitCode: null,
    notes: "",
    assignedEmployeeId: null,
    createdByUserId: JSON.parse(localStorage.getItem("userId") || "0"),
    orderId: orderId,
    file: null,
  });

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updatedItem, setUpdatedItem] = useState({
    itemName: "",
    quantity: 1,
    notes: "",
    status: "",
    unitPrice: 0,
    taxRate: null,
    unitCode: null,
    assignedEmployeeId: null,
    updatedByUserId: JSON.parse(localStorage.getItem("userId") || "0"),
    orderId: orderId,
    file: null,
    hasFile: false,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Use error handling hooks
  const { loading: orderDataLoading, error: orderDataError, execute: executeOrderDataCall } = useApiCall();
  const { loading: employeesLoading, error: employeesError, execute: executeEmployeesCall } = useApiCall();
  const { loading: statusUpdateLoading, execute: executeStatusUpdate } = useApiCall();
  const { loading: downloadLoading, execute: executeDownload } = useApiCall();
  const { loading: excelUploadLoading, execute: executeExcelUpload } = useApiCall();
  const { loading: pdfDownloadLoading, execute: executePdfDownload } = useApiCall();
  const { loading: createItemLoading, execute: executeCreateItem } = useApiCall();
  const { loading: updateItemLoading, execute: executeUpdateItem } = useApiCall();
  const { loading: deleteItemLoading, execute: executeDeleteItem } = useApiCall();


  useEffect(() => {
    fetchOrderAndItems();
    fetchEmployees();
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrderAndItems = async () => {
    if (!orderId) {
      return;
    }

    try {
      const [orderResponse, itemsResponse] = await Promise.all([
        executeOrderDataCall(() => getOrderById(orderId)).catch(err => {
          console.error("Error fetching order:", err);
          return null;
        }),
        executeOrderDataCall(() => getOrderItemsByOrderId(orderId)).catch(err => {
          console.error("Error fetching order items:", err);
          return { content: [] };
        })
      ]);

      setOrder(orderResponse || null);
      setOrderItems(itemsResponse?.content || []);
    } catch (err) {
      console.error("Error fetching order data:", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const adminId = localStorage.getItem("userId");
      const response = await executeEmployeesCall(
        () => getEmployeeByAdminId(adminId)
      );
      setEmployees(response?.content || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    }
  };

  const handleExcelUpload = async (file) => {
    if (!file) {
      toast.error("Lütfen bir Excel dosyası seçin");
      return;
    }

    try {
      await executeExcelUpload(
        () => uploadOrderItemsExcel(orderId, file)
      );
      toast.success("Excel dosyası başarıyla yüklendi!");
      fetchOrderAndItems(); // Refresh list
    } catch (err) {
      console.error("Excel upload error:", err);
      // Error handled by hook
    }
  };

  const handlePdfDownload = async () => {
    try {
      await executePdfDownload(
        () => downloadOrderPdf(orderId)
      );
      toast.success("PDF dosyası indiriliyor...");
    } catch (err) {
      console.error("PDF download error:", err);
      // Error handled by hook
    }
  };

  const handleOrderStatusChange = async (newStatus) => {
    try {
      await executeStatusUpdate(
        () => updateOrderStatus(orderId, newStatus)
      );
      setOrder((prevOrder) => ({
        ...prevOrder,
        status: newStatus,
      }));
      toast.success("Sipariş durumu güncellendi!");
    } catch (err) {
      console.error("Order status update error:", err);
      // Error handled by hook
    }
  };

  const handleItemStatusChange = async (itemId, newStatus) => {
    try {
      await executeStatusUpdate(
        () => updateOrderItemStatus(itemId, newStatus)
      );
      setOrderItems((items) =>
        items.map((item) =>
          item.id === itemId ? { ...item, status: newStatus } : item
        )
      );
      toast.success("Kalem durumu güncellendi!");
    } catch (err) {
      console.error("Item status update error:", err);
      // Error handled by hook
    }
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({
      ...prev,
      [name]: name === "quantity" ? parseInt(value) || "" : value,
    }));
  };

  const handleDownloadFile = async (itemId) => {
    try {
      await executeDownload(
        () => downloadOrderItemFile(itemId)
      );
      toast.success("Dosya başarıyla indirildi!");
    } catch (err) {
      console.error("File download error:", err);
      // Error handled by hook
    }
  };

  const handleCreateSubmit = async (file) => {
    try {
      const itemToCreate = { ...newItem };
      if (file) {
        itemToCreate.file = file;
      }

      await executeCreateItem(
        () => createOrderItem(itemToCreate)
      );

      setShowCreateModal(false);
      toast.success("Sipariş kalemi başarıyla oluşturuldu!");
      fetchOrderAndItems();

      // Reset form
      setNewItem({
        itemName: "",
        quantity: 1,
        unitPrice: "",
        taxRate: null,
        unitCode: null,
        notes: "",
        assignedEmployeeId: null,
        createdByUserId: JSON.parse(localStorage.getItem("userId") || "0"),
        orderId: orderId,
        file: null,
      });
    } catch (err) {
      console.error("Create item error:", err);
      // Error handled by hook
    }
  };

  const handleUpdateModalOpen = (item) => {
    setUpdatedItem({
      ...item,
      assignedEmployeeId: item.assignedEmployee?.id || null,
      updatedByUserId: JSON.parse(localStorage.getItem("userId") || "0"),
      hasFile: item.hasFile || false,
      fileName: item.fileName || null,
      file: null,
    });
    setShowUpdateModal(true);
  };

  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedItem((prev) => ({
      ...prev,
      [name]: name === "quantity" ? parseInt(value) || "" : value,
    }));
  };

  const handleUpdateSubmit = async (file) => {
    try {
      const itemToUpdate = { ...updatedItem };
      if (file) {
        itemToUpdate.file = file;
      }

      await executeUpdateItem(
        () => updateOrderItem(updatedItem.id, itemToUpdate)
      );

      setShowUpdateModal(false);
      toast.success("Sipariş kalemi başarıyla güncellendi!");
      fetchOrderAndItems();
    } catch (err) {
      console.error("Update item error:", err);
      // Error handled by hook
    }
  };

  const handleDeleteModalOpen = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete?.id) return;

    try {
      await executeDeleteItem(
        () => deleteOrderItem(itemToDelete.id)
      );

      setShowDeleteModal(false);
      setItemToDelete(null);
      toast.success("Sipariş kalemi başarıyla silindi!");
      fetchOrderAndItems();
    } catch (err) {
      console.error("Delete item error:", err);
      // Error handled by hook
    }
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleRetry = () => {
    fetchOrderAndItems();
  };

  const handleRetryEmployees = () => {
    fetchEmployees();
  };

  const columns = [
    {
      title: "Kalem İsmi",
      dataIndex: "itemName",
      key: "itemName",
    },
    {
      title: "Adet",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Birim",
      dataIndex: "unitCode",
      key: "unitCode",
      align: "center",
      render: (val) => getUnitCodeLabel(val),
    },
    {
      title: "Adet Fiyatı",
      dataIndex: "unitPrice",
      key: "unitPrice",
      render: (price) => formatPrice(price),
    },
    {
      title: "Toplam",
      key: "total",
      render: (text, record) => formatTotalPrice(record.quantity, record.unitPrice),
    },
    {
      title: "Durum",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <StatusSelect
          type="orderItem"
          value={status}
          onChange={(value) => handleItemStatusChange(record.id, value)}
          loading={statusUpdateLoading}
          disabled={statusUpdateLoading}
        />
      ),
    },
    {
      title: "Sorumlu Personel",
      dataIndex: "assignedEmployee",
      key: "assignedEmployee",
      render: (employee) => employee ? employee.name : "-",
    },
    {
      title: "Notlar",
      dataIndex: "notes",
      key: "notes",
      render: (notes) => notes || "-",
    },
    {
      title: "Dosya",
      dataIndex: "hasFile",
      key: "hasFile",
      render: (hasFile, record) =>
        hasFile ? (
          <Tooltip title="PDF'i indir">
            <Button
              type="text"
              icon={<FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />}
              onClick={() => handleDownloadFile(record.id)}
              loading={downloadLoading}
              disabled={downloadLoading}
            />
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Eylemler",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            onClick={() => handleUpdateModalOpen(record)}
            disabled={updateItemLoading || deleteItemLoading}
          >
            Düzenle
          </Button>
          <Button 
            type="primary" 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteModalOpen(record)}
            disabled={updateItemLoading || deleteItemLoading}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  // Handle invalid order ID
  if (!orderId) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="warning"
          title="Geçersiz Sipariş"
          subTitle="Sipariş ID'si bulunamadı."
          extra={
            <Link to="/admin/orders">
              <Button type="primary">
                Siparişlere Dön
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  // Handle major errors
  if (orderDataError && !orderDataLoading && !order && orderItems.length === 0) {
    return (
      <div style={{ padding: "24px" }}>
        <Result
          status="error"
          title="Sipariş Yüklenemedi"
          subTitle="Sipariş detayları yüklenirken bir hata oluştu. Lütfen tekrar deneyin."
          extra={[
            <Button type="primary" key="retry" icon={<ReloadOutlined />} onClick={handleRetry}>
              Tekrar Dene
            </Button>,
            <Link to="/admin/orders" key="back">
              <Button>
                Siparişlere Dön
              </Button>
            </Link>
          ]}
        />
      </div>
    );
  }

  // Show loading state
  if (orderDataLoading && !order && orderItems.length === 0) {
    return (
      <div style={{ textAlign: "center", marginTop: "5rem" }}>
        <Spin tip="Sipariş detayları yükleniyor..." size="large" />
      </div>
    );
  }

  const totalAmount = orderItems.reduce(
    (total, item) => {
      try {
        return total + item.quantity * (item.unitPrice || 0);
      } catch (error) {
        return total;
      }
    },
    0
  );

  return (
    <div style={{ padding: "24px" }}>
      <Card
        title={
          <Space direction="horizontal">
            <Title level={4} style={{ margin: 0 }}>Sipariş Detayları - #{orderId}</Title>
            {order?.publicToken && (
              <Typography.Text code copyable={{ text: order.publicToken }}>
                {order.publicToken}
              </Typography.Text>
            )}
            {order && (
              <StatusSelect
                type="order"
                value={order?.status}
                onChange={handleOrderStatusChange}
                width={150}
                loading={statusUpdateLoading}
                disabled={statusUpdateLoading}
              />
            )}
            {orderDataError && !order && (
              <Alert
                message="Sipariş bilgileri yüklenemedi"
                type="warning"
                size="small"
                showIcon
              />
            )}
          </Space>
        }
        extra={
          <Space>
            <Button 
              type="primary" 
              onClick={() => setShowCreateModal(true)}
              loading={createItemLoading}
              disabled={createItemLoading}
            >
              Kalem Ekle
            </Button>
            <Link to="/admin/orders">
              <Button>Siparişlere Dön</Button>
            </Link>
          </Space>
        }
      >
        {/* Show warning for employees error */}
        {employeesError && (
          <Alert
            message="Çalışan listesi yüklenemedi"
            description="Kalem atama işlevselliği etkilenebilir."
            type="warning"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={handleRetryEmployees}>
                Tekrar Dene
              </Button>
            }
          />
        )}

        {/* Show warning if order data error but we have items */}
        {orderDataError && orderItems.length > 0 && (
          <Alert
            message="Veriler güncel olmayabilir"
            description="Son güncellemede bir hata oluştu."
            type="warning"
            showIcon
            closable
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={handleRetry}>
                Yenile
              </Button>
            }
          />
        )}

        <Table
          dataSource={orderItems}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: true }}
          footer={() => (
            <Row justify="end">
              <Col>
                <Typography.Text strong>
                  Toplam: {formatPrice(totalAmount)}
                </Typography.Text>
              </Col>
            </Row>
          )}
        />
      </Card>

      {/* Excel Upload & PDF Download Section */}
      <ExcelUploadSection
        orderId={orderId}
        onUpload={handleExcelUpload}
        onDownloadPdf={handlePdfDownload}
        isUploading={excelUploadLoading}
        isDownloadingPdf={pdfDownloadLoading}
      />

      <CreateOrderItemModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        item={newItem}
        onChange={handleCreateInputChange}
        onSubmit={handleCreateSubmit}
        employees={employees}
        loadingEmployees={employeesLoading}
        loading={createItemLoading}
      />

      <UpdateOrderItemModal
        show={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        item={updatedItem}
        onChange={handleUpdateInputChange}
        onSubmit={handleUpdateSubmit}
        employees={employees}
        loadingEmployees={employeesLoading}
        loading={updateItemLoading}
      />

      <ConfirmDeleteModal
        visible={showDeleteModal}
        onCancel={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        title="Sipariş Kalemi Sil"
        itemName={itemToDelete?.itemName}
        itemDetails={
          itemToDelete && (
            <>
              <div><Text strong>Adet:</Text> {itemToDelete.quantity}</div>
              <div><Text strong>Fiyat:</Text> {formatPrice(itemToDelete.unitPrice)}</div>
              {itemToDelete.notes && <div><Text strong>Not:</Text> {itemToDelete.notes}</div>}
            </>
          )
        }
        loading={deleteItemLoading}
        warningText="Bu işlem geri alınamaz. Sipariş kalemi kalıcı olarak silinecektir."
      />
    </div>
  );
};

export default AdminOrderItemsPage;